import React, { useState } from 'react';
import axios from 'axios';
import { Send, Loader2, CheckCircle, AlertCircle, User, Mail, Building, Phone, MessageSquare } from 'lucide-react';
import DataProtectionCheckbox from './DataProtectionCheckbox';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function LeadForm({ origen = "formulario_web", onSuccess }) {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    cargo: 'Responsable RRHH',
    empresa: '',
    telefono: '',
    mensaje: ''
  });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API_URL}/api/leads`, {
        ...formData,
        origen
      });
      setSuccess(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al enviar el formulario');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-12" data-testid="lead-success">
        <CheckCircle className="mx-auto text-emerald-600 mb-4" size={48} />
        <h3 className="text-2xl font-bold text-slate-900 mb-2">¡Solicitud enviada!</h3>
        <p className="text-slate-600">
          Nos pondremos en contacto contigo pronto.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="lead-form">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 flex items-start gap-3" data-testid="lead-error">
          <AlertCircle className="text-red-600 flex-shrink-0" size={18} />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="form-group">
        <label className="form-label">Nombre completo *</label>
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            name="nombre"
            value={formData.nombre}
            onChange={handleChange}
            className="form-input pl-10"
            placeholder="Su nombre"
            required
            data-testid="lead-nombre"
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Email profesional *</label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="form-input pl-10"
            placeholder="correo@empresa.com"
            required
            data-testid="lead-email"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="form-group">
          <label className="form-label">Cargo *</label>
          <select
            name="cargo"
            value={formData.cargo}
            onChange={handleChange}
            className="form-input"
            required
            data-testid="lead-cargo"
          >
            <option value="Responsable RRHH">Responsable RRHH</option>
            <option value="Gerencia / PYME">Gerencia / PYME</option>
            <option value="Técnico de Prevención (SPA/SPP)">Técnico de Prevención</option>
            <option value="Asesor laboral">Asesor laboral</option>
            <option value="Otro">Otro</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Teléfono</label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="tel"
              name="telefono"
              value={formData.telefono}
              onChange={handleChange}
              className="form-input pl-10"
              placeholder="+34 600 000 000"
              data-testid="lead-telefono"
            />
          </div>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Empresa</label>
        <div className="relative">
          <Building className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            name="empresa"
            value={formData.empresa}
            onChange={handleChange}
            className="form-input pl-10"
            placeholder="Nombre de su empresa"
            data-testid="lead-empresa"
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Mensaje (opcional)</label>
        <div className="relative">
          <MessageSquare className="absolute left-3 top-3 text-slate-400" size={18} />
          <textarea
            name="mensaje"
            value={formData.mensaje}
            onChange={handleChange}
            className="form-input pl-10 min-h-[100px] resize-none"
            placeholder="¿En qué podemos ayudarle?"
            data-testid="lead-mensaje"
          />
        </div>
      </div>

      <DataProtectionCheckbox
        checked={privacyAccepted}
        onChange={setPrivacyAccepted}
        id="lead-privacy-consent"
      />

      <button
        type="submit"
        disabled={loading || !privacyAccepted}
        className="btn btn-accent w-full disabled:opacity-50"
        data-testid="lead-submit"
      >
        {loading ? (
          <Loader2 className="animate-spin" size={18} />
        ) : (
          <>
            <Send size={18} />
            Solicitar información
          </>
        )}
      </button>
    </form>
  );
}
